import { List, Map } from "immutable";
import { each } from "lodash";

import syncApplications from "../../constants/syncApplications";
import syncDocumentTypes from "../../constants/syncDocumentTypes";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import immutableRecords from "../../types/immutableRecords";
import { listToIndexMap } from "../../utils/immutableExtensions";
import syncDocumentFromEntityType from "./syncDocumentFromEntityType";
import syncDocumentFromItem from "./syncDocumentFromItem";

const syncRepresentationOfFlatFolder = (
  folder,
  itemsById,
  itemIndexByType,
  typesById,
  itemIdsByType,
  localToExternalIds
) => {
  let documentTypes = {};

  // Get the list of items for this type
  const type = typesById.get(folder.mainType);
  const itemsForType = type ? itemIdsByType.get(type.id) : null;
  if (!type || !itemsForType) {
    return;
  }

  const indexMap = itemIndexByType.get(type.id);

  const sortedItemsForType = itemsForType.sort((a, b) => indexMap.get(a) - indexMap.get(b));

  // For each item, update the documents map and the documentTree
  const children = List(
    sortedItemsForType.map(id => {
      // Get the item
      // Find what ID we are using in this tree
      const externalId = localToExternalIds.has(id) ? localToExternalIds.get(id) : id;

      // Update the documents map
      documentTypes[id] = syncDocumentTypes.TEXT;
      // And return the Tree node
      return immutableRecords.TreeRecord({
        id: externalId
      });
    })
  );
  const documentTree = immutableRecords.TreeRecord({
    children,
    id: folder.folderId
  });
  return {
    documentTree,
    documentTypes
  };
};

const convertItemGroupToTree = (
  groupId,
  groupExternalId,
  documentTypes,
  levelIndex,
  itemsById,
  localToExternalIds,
  itemGroupsById,
  folderSettings
) => {
  const itemGroup = itemGroupsById.get(groupId);
  const children = itemGroup
    ? List(
      itemGroup.map(id => {
        // Get the item
        const item = itemsById.get(id);
        const itemType = item.type;
        const documentType =
            folderSettings.levels.size > levelIndex
              ? folderSettings.levels.get(levelIndex).documentType
              : folderSettings.defaultDocumentType;
        const folderType =
            folderSettings.levels.size > levelIndex
              ? folderSettings.levels.get(levelIndex).folderType
              : folderSettings.defaultFolderType;

        // If this type is not allowed in this list at this position, then ignore it!
        if (itemType !== documentType && itemType !== folderType) {
          return null;
        }

        // Find what ID we are using in this tree
        const externalId = localToExternalIds.has(id) ? localToExternalIds.get(id) : id;

        // And call recursively for children
        const documentTree = convertItemGroupToTree(
          id,
          externalId,
          documentTypes,
          levelIndex + 1,
          itemsById,
          localToExternalIds,
          itemGroupsById,
          folderSettings
        ).set("id", externalId);

        let documentOrFolder = null;

        if (documentType === folderType) {
          documentOrFolder = documentTree.children.size ? syncDocumentTypes.FOLDER : syncDocumentTypes.TEXT;
        } else if (documentType === itemType) {
          documentOrFolder = syncDocumentTypes.TEXT;
        } else {
          documentOrFolder = syncDocumentTypes.FOLDER;
        }
        // Update the documents map
        documentTypes[id] = documentOrFolder;

        return documentTree;
      })
    ).filter(t => t)
    : List();
  // And return the Tree node
  return immutableRecords.TreeRecord({
    id: groupExternalId,
    children
  });
};

const syncRepresentationOfNestedFolder = (
  folder,
  itemsById,
  itemIndexByType,
  superAndChildrenById,
  typesById,
  orderedTypeIds,
  itemIdsByType,
  localToExternalIds,
  app
) => {
  let documentTypes = {};

  // Get the list of items for this type
  const type = typesById.get(folder.mainType);
  const itemsForType = type ? itemIdsByType.get(type.id) : null;
  if (!type || !itemsForType) {
    return;
  }

  const reverseFolderDocumentOrder = app === syncApplications.ULYSSES;

  const superTypeIds = typeSelectors.getAllSuperTypesFromTypeMap(typesById, type.id);
  const itemGroupsById = itemSelectors.getItemTreeGroupsForTypeAndSuperTypes(
    itemIdsByType,
    itemIndexByType,
    orderedTypeIds,
    superTypeIds,
    superAndChildrenById,
    type.id,
    reverseFolderDocumentOrder
  );
  const rootId = "";
  const documentTree = convertItemGroupToTree(
    rootId,
    rootId,
    documentTypes,
    0,
    itemsById,
    localToExternalIds,
    itemGroupsById,
    folder
  ).set("id", folder.folderId);

  return {
    documentTree,
    documentTypes
  };
};

const getSyncDocuments = (
  documentTypes,
  itemsById,
  typesById,
  relationshipsById,
  propertiesById,
  referencesById,
  colorsById,
  localToExternalIds,
  syncSettings,
  tagsById,
  calendar,
  externalRep
) => {
  let documents = {};

  each(documentTypes, (documentOrFolder, id) => {
    const externalId = localToExternalIds.has(id) ? localToExternalIds.get(id) : id;
    // TODO: Need to pass in documentType!

    documents[externalId] = syncDocumentFromItem(
      itemsById.get(id),
      externalId,
      documentOrFolder,
      syncSettings,
      itemsById,
      typesById,
      relationshipsById,
      propertiesById,
      referencesById,
      colorsById,
      tagsById,
      calendar,
      externalRep.customFields
    );
  });

  return documents;
};

/**
 *
 * @param {Map} itemsById
 * @param {Map} superAndChildrenById
 * @param {Map} typesById
 * @param {List} orderedTypeIds
 * @param {Map} itemIdsByType
 * @param {Map} relationshipsById
 * @param {Map} propertiesById
 * @param {Map} referencesById
 * @param {Map} colorsById
 * @param {Map} localToExternalIds
 * @param {SyncSettingsRecord} settings
 * @param {Map} tagsById
 * @param {CalendarRecord} calendar
 * @param {SyncRepresentationRecord} externalRep
 */
const syncRepresentationOfEntityFolders = (
  itemsById,
  superAndChildrenById,
  typesById,
  orderedTypeIds,
  itemIdsByType,
  relationshipsById,
  propertiesById,
  referencesById,
  colorsById,
  localToExternalIds,
  settings,
  tagsById,
  calendar,
  externalRep
) => {
  let rootChildren = [];
  let documentTypes = {};
  let documents = {};

  const itemIndexByType = itemIdsByType.map(listToIndexMap);
  settings.entityFolders.forEach(folder => {
    const type = typesById.get(folder.mainType);
    if (folder.flattened) {
      const syncRepresentation = syncRepresentationOfFlatFolder(
        folder,
        itemsById,
        itemIndexByType,
        typesById,
        itemIdsByType,
        localToExternalIds
      );
      if (syncRepresentation) {
        const { documentTree, documentTypes: folderDocumentTypes } = syncRepresentation;
        rootChildren.push(documentTree);
        Object.assign(documentTypes, folderDocumentTypes);
      }
    } else {
      const syncRepresentation = syncRepresentationOfNestedFolder(
        folder,
        itemsById,
        itemIndexByType,
        superAndChildrenById,
        typesById,
        orderedTypeIds,
        itemIdsByType,
        localToExternalIds,
        settings.app
      );
      if (syncRepresentation) {
        const { documentTree, documentTypes: folderDocumentTypes } = syncRepresentation;
        rootChildren.push(documentTree);
        Object.assign(documentTypes, folderDocumentTypes);
      }
    }
    documents[folder.folderId] = syncDocumentFromEntityType(type, folder.folderId);
  });

  Object.assign(
    documents,
    getSyncDocuments(
      documentTypes,
      itemsById,
      typesById,
      relationshipsById,
      propertiesById,
      referencesById,
      colorsById,
      localToExternalIds,
      settings,
      tagsById,
      calendar,
      externalRep
    )
  );
  const documentTree = immutableRecords.TreeRecord({
    children: List(rootChildren)
  });

  const result = immutableRecords.SyncRepresentationRecord({
    documentTree,
    documents: Map(documents)
  });

  return result;
};

export default syncRepresentationOfEntityFolders;
